import { useEffect, useState } from 'react';
import config from '../config';
import { readSseStream } from '../helpers/sse';

const RECONNECT_MS = 3000;
/**
 * A driver not heard from in this long is dropped from the board — the same
 * window the server applies to its snapshot, so a reconnect and a long-open
 * stream show the same set of cars.
 */
export const DRIVER_STALE_MS = 15 * 60 * 1000;
/**
 * Fixes are folded in as they arrive but handed to React at most this often.
 * With many drivers navigating, `driver` events land several times a second;
 * re-rendering the map for each one buys nothing a once-a-second redraw
 * doesn't, at a fraction of the work.
 */
const FLUSH_MS = 1000;

const time = (iso) => Date.parse(iso) || 0;

/**
 * The last board seen, kept across mounts. Opening a trip unmounts the board
 * and closes its stream, and coming back would otherwise show an empty map
 * until the new connection's snapshot lands. A remount starts from this
 * (minus drivers gone stale in the meantime) and the snapshot replaces it.
 */
let lastBoard = {};

/**
 * Folds one fix into the board, keyed by driver. A fix older than the one
 * already held is ignored — the two pipelines (trip pings, background fixes)
 * can deliver out of order, and the board should never step a car backwards.
 * Returns the same object when nothing changed, so callers can skip a render.
 */
export const applyDriverEvent = (drivers, event) => {
  if (!event || !event.driverId) return drivers;
  const held = drivers[event.driverId];
  if (held && time(held.timestamp) > time(event.timestamp)) return drivers;
  return { ...drivers, [event.driverId]: event };
};

/** Drops drivers whose last fix is older than {@link DRIVER_STALE_MS}. */
export const pruneStaleDrivers = (drivers, now = Date.now()) => {
  const fresh = Object.entries(drivers).filter(([, d]) => now - time(d.timestamp) <= DRIVER_STALE_MS);
  return fresh.length === Object.keys(drivers).length ? drivers : Object.fromEntries(fresh);
};

/**
 * Streams every live driver's latest position off `/admin/live/drivers`: on
 * a trip (precise pings, tagged with the trip) or between trips (background
 * fixes, `tripId: null`). One stream for the whole board, not one per driver.
 *
 * `enabled: false` closes the stream — the Trips map only wants it while no
 * trip is selected.
 */
const useLiveDrivers = ({ enabled = true } = {}) => {
  const [drivers, setDrivers] = useState(() => (enabled ? pruneStaleDrivers(lastBoard) : {}));
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setDrivers({});
      setConnected(false);
      return undefined;
    }

    const controller = new AbortController();
    let retry = null;
    let stopped = false;
    // The live board, mutated as events arrive and flushed to React on a timer.
    // Fixes that land before the snapshot fold onto the cached board.
    let board = pruneStaleDrivers(lastBoard);
    let dirty = false;
    // Covers `enabled` flipping back on, which had cleared the board.
    setDrivers(board);

    const flush = setInterval(() => {
      const pruned = pruneStaleDrivers(board);
      if (pruned !== board) {
        board = pruned;
        dirty = true;
      }
      if (dirty) {
        dirty = false;
        lastBoard = board;
        setDrivers(board);
      }
    }, FLUSH_MS);

    const connect = async () => {
      try {
        await readSseStream({
          url: `${config.api.baseUrl}/admin/live/drivers`,
          signal: controller.signal,
          onOpen: () => setConnected(true),
          onEvent: ({ event, data }) => {
            if (event === 'snapshot' && Array.isArray(data)) {
              // A (re)connect replaces the board: the snapshot is the truth.
              board = data.reduce(applyDriverEvent, {});
              dirty = true;
            } else if (event === 'driver') {
              const next = applyDriverEvent(board, data);
              if (next !== board) {
                board = next;
                dirty = true;
              }
            }
          },
        });
      } catch (e) {
        if (controller.signal.aborted) return;
      } finally {
        if (!stopped) setConnected(false);
      }
      if (!stopped) retry = setTimeout(connect, RECONNECT_MS);
    };

    connect();

    return () => {
      // Fixes from the last partial flush interval still count.
      lastBoard = board;
      stopped = true;
      clearTimeout(retry);
      clearInterval(flush);
      controller.abort();
    };
  }, [enabled]);

  return { drivers, connected };
};

export default useLiveDrivers;
